import { useGetIdentity, useTranslation } from "@refinedev/core";
import { Breadcrumbs } from "components/breadcrumbs";
import { FullScreenLoading } from "components/fullScreenLoading";
import { IUserData } from "interfaces";
import { get } from "lodash";
import React, { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { useNavigate, useParams } from "react-router-dom";
import ApiService from "utills/ApiService";
import useWindowWidth from "utills/InitWidthDevice";
import { TemplateDataType } from "../../types";
import Button from "components/button";
import { PrinterIcon } from "@heroicons/react/outline";
import { _openUrlOnNewTab } from "utills/helpers";
import { CollapsibleSection } from "components/collapsibleSection";

const WPSAttendeesAttractionsDetail = () => {
  const params = useParams();
  const navigate = useNavigate();
  const windowWidth = useWindowWidth();
  const { data: user } = useGetIdentity<IUserData>();
  const { translate, getLocale } = useTranslation();
  const language = getLocale();

  const [isMobile, setIsMobile] = useState(false);
  const [loading, setLoading] = useState(false);
  const [participant, setParticipant] = useState<any>(null);

  const breadcrumbItems = [
    {
      label: translate("attendees"),
      href: "/attendees/attractions",
    },
    {
      label: translate("attraction-attendees-detail"),
      href: `/attendees/attractions/${params?.id}/detail`,
    },
  ];

  const handleGetDetail = async (token: string, id: string) => {
    setLoading(true);

    try {
      const res = await ApiService.get(`attraction-participants/${id}`, token);
      console.log("Detail participants:", res);

      setParticipant(res);
    } catch (error: any) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleNavigateOrderDetail = (orderCode: string) => {
    navigate(`/orders/${orderCode}`);
  };

  useEffect(() => {
    if (windowWidth && windowWidth < 1024) {
      setIsMobile(true);
    } else {
      setIsMobile(false);
    }
  }, [windowWidth]);

  useEffect(() => {
    if (user?.token && params?.id) {
      handleGetDetail(user?.token, params?.id);
    }
  }, [user, params]);

  return (
    <div className="w-full">
      {loading && <FullScreenLoading />}

      <Breadcrumbs
        type="wps"
        breadcrumbItems={breadcrumbItems}
        disableBorder={true}
      />

      <div className="w-full">
        {/* Main Content */}
        {!loading && participant && (
          <div className="mb-[16px] p-4 flex flex-col gap-5">
            {/* General information */}
            <CollapsibleSection
              defaultOpen={true}
              label={translate("general-information")}
            >
              <div
                  className={`w-full p-6 grid grid-cols-1 lg:grid-cols-2 gap-4`}
              >
                <div className="w-full flex">
                  <span className="w-[200px] max-w-[200px] text-[14px] font-[500]">
                    {translate("status")}
                  </span>

                  {participant?.participant?.is_active ? (
                    <span className="w-[118px] py-1 rounded-[12px] font-medium flex justify-center bg-[#DCFCE7] text-[#1D813F]">
                      {translate("active")}
                    </span>
                  ) : (
                    <span className="w-[118px] py-1 rounded-[12px] font-medium flex justify-center bg-[#FFCFDD] text-[#9D0520]">
                      {translate("inactive")}
                    </span>
                  )}
                </div>

                <div className="w-full flex">
                  <span className="w-[200px] max-w-[200px] text-[14px] font-[500]">
                    {translate("ticket-no")}
                  </span>
                  <span className="text-[14px] font-[400]">
                    {participant?.participant?.ticket_no}
                  </span>
                </div>

                <div className="w-full flex">
                  <span className="w-[200px] max-w-[200px] text-[14px] font-[500]">
                    {translate("event-name")}
                  </span>
                  <span className="text-[14px] font-[400]">
                    {get(
                      participant?.participant,
                      `event_name_${language}`,
                      ""
                    )}
                  </span>
                </div>

                <div className="w-full flex">
                  <span className="w-[200px] max-w-[200px] text-[14px] font-[500]">
                    {translate("ticket-name")}
                  </span>
                  <span className="text-[14px] font-[400]">
                    {get(
                      participant?.participant,
                      `product_name_${language}`,
                      ""
                    )}
                  </span>
                </div>

                <div className="w-full flex">
                  <span className="w-[200px] max-w-[200px] text-[14px] font-[500]">
                    {translate("seat-number")}
                  </span>
                  <span className="text-[14px] font-[400]">
                    {participant?.participant?.seat_number}
                  </span>
                </div>

                <div className="w-full flex">
                  <span className="w-[200px] max-w-[200px] text-[14px] font-[500]">
                    {translate("order-code")}
                  </span>

                  {participant?.participant?.parent_ref_id && (
                    <span
                      className="text-[#011BB6] text-[14px] font-[400] hover:cursor-pointer"
                      onClick={() => {
                        handleNavigateOrderDetail(
                          participant?.participant?.parent_ref_id
                        );
                      }}
                    >
                      {participant?.participant?.parent_ref_id}
                    </span>
                  )}
                </div>
              </div>
            </CollapsibleSection>

            {/* Registration information */}
            <CollapsibleSection
              defaultOpen={true}
              label={translate("registration-information")}
            >
              <div className="p-6">
                {Object.entries(
                  (participant?.template_data as TemplateDataType) || {}
                ).map(([key, value]: [string, TemplateDataType], index, arr) => {
                  const isLast = index === arr.length - 1;

                  return (
                    <div key={key} className="mb-[30px] flex flex-col gap-5">
                      {/* <div className="text-lg font-semibold mb-2">
                        {get(value, `label_${language}`, "")}
                      </div> */}

                      <h3 className="my-0 text-[0.95rem] font-bold text-gray-600">
                        {get(value, `label_${language}`, "")}
                      </h3>

                      {/* Form field render */}
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                        {value?.fields?.map((field) => {
                          const fieldValue =
                            participant?.participant[field?.key] || "";

                          return (
                            <div
                              key={field?.key}
                              className="flex items-start"
                            >
                              <div className="w-[200px] max-w-[200px] text-[14px] font-[500]">
                                {get(field, `label_${language}`, "")}
                              </div>

                              {field?.key?.indexOf("phone") === -1 ? (
                                <span className="text-[14px] font-[400]">
                                  {fieldValue}
                                </span>
                              ) : (
                                <span className="text-[14px] font-[400]">
                                  +
                                  {get(
                                    participant?.participant,
                                    `${field?.key}_area_code`,
                                    ""
                                  )}{" "}
                                  {fieldValue}
                                </span>
                              )}
                            </div>
                          );
                        })}
                      </div>

                      {!isLast && <div className="w-full h-[1px] bg-gray-200"></div>}
                    </div>
                  );
                })}
              </div>
            </CollapsibleSection>
          </div>
        )}
      </div>
    </div>
  );
};

export default WPSAttendeesAttractionsDetail;
